import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { companiesListHref } from "../../lib/companies-back";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyDetail } from "../../lib/crm-types";
import { ArchivedBanner } from "../../components/crm/ArchiveCompany";
import { AiSummaryPanel } from "../../components/crm/AiSummaryPanel";
import { AtRiskPanel } from "../../components/crm/AtRiskPanel";
import { CommercialPanels } from "../../components/crm/CommercialPanels";
import { ContactsPanel } from "../../components/crm/ContactsPanel";
import { CustomFieldsPanel } from "../../components/crm/CustomFieldsPanel";
import { FinancialsPanel } from "../../components/crm/FinancialsPanel";
import { optionLabel } from "../../components/crm/FieldPickers";
import { FollowPanel } from "../../components/crm/FollowPanel";
import { GroupPanel } from "../../components/crm/GroupPanel";
import { PerformancePanel } from "../../components/crm/PerformancePanel";
import { SalesCallsPanel } from "../../components/crm/SalesCallsPanel";
import { Timeline } from "../../components/crm/Timeline";
import type { ComposerMode } from "../../components/crm/ActivityComposer";
import { cn } from "../../components/ui/cn";
import type { User } from "../../types";

/**
 * The company page on a phone, for somebody on the road.
 *
 * The desktop page is three columns of everything Pulse knows, under a header
 * that never scrolls. Stacked onto a phone that header was a third of the
 * screen before anything had been read -- name, three picklists, six buttons
 * and a row of facts, pinned there -- and below it came the duplicate queue,
 * the group, every detail field and the contacts before the history at all.
 *
 * A rep in the car park wants four things, in this order: to write down what
 * just happened, to read what happened before, the handful of details that
 * get them in the door -- the number, the address, who manages it -- and the
 * way through to Team. So the page is those, and it all scrolls:
 *
 * - **Logging** is a bar along the bottom, under the thumb wherever the page
 *   is scrolled to. That is what the pinned header was for; the bottom of the
 *   screen does it without costing the top.
 * - **The header** is the name, one line of who and which account, and Team
 *   beside the name -- a pill, as on the list, rather than a full-width button
 *   under it, which made the header a row taller to say one word.
 * - **Key details** are things to tap: call, email, find on the map.
 * - **Notes and history** are the timeline, at full width.
 *
 * Everything else -- the credit file, follow-ups, custom fields, the AI
 * summary, editing and archiving -- is still here, folded under one "More"
 * at the end. Nothing is lost; it is just not between somebody and the note
 * they came for. Stage, type and priority are read here and changed on a
 * desktop, where the pickers are.
 */
export function CompanyPhone({
  company,
  user,
  taskBlockedReason,
  composeMode,
  onComposeMode,
  onEdit,
  onSalesCall,
  onArchive,
}: {
  company: CompanyDetail;
  user: User;
  taskBlockedReason: string | null;
  composeMode: ComposerMode | null;
  onComposeMode: (mode: ComposerMode | null) => void;
  onEdit: () => void;
  onSalesCall: () => void;
  onArchive: (() => void) | null;
}) {
  const archived = company.archivedAt !== null;

  return (
    /* A column, so the content can take the slack and the log bar sits on the
       bottom of the screen even for a company with three lines of history --
       sticky alone only pins it once the page is long enough to scroll, and a
       short one left it floating halfway up. */
    <div className="flex h-full flex-col overflow-y-auto overscroll-contain bg-app">
      {archived ? <ArchivedBanner company={company} isAdmin={user.role === "admin"} /> : null}

      <header className="border-b border-line bg-surface-2 px-4 pb-3 pt-2">
        {/* Back to the list as it was filtered: an installed app has no Back
            button, and this is the one somebody reaches for. */}
        <div className="-ml-2 flex items-center gap-1 text-xs text-muted">
          <Link
            to={companiesListHref()}
            className="flex h-9 items-center rounded-md px-2 hover:text-brand-600"
          >
            ‹ Companies
          </Link>
          {company.parent ? (
            <Link
              to={`/crm/companies/${company.parent.id}`}
              className="truncate hover:text-brand-600"
            >
              / {company.parent.name}
            </Link>
          ) : null}
        </div>
        <div className="flex items-start gap-3">
          <h1 className="min-w-0 flex-1 break-words text-lg font-semibold leading-tight text-content">
            {company.name}
          </h1>
          {company.externalUrl ? (
            <a
              href={company.externalUrl}
              target="_blank"
              rel="noreferrer"
              title="Open this account in Team"
              className="flex h-8 shrink-0 items-center rounded-md border border-line bg-surface px-2.5 text-xs font-medium text-content active:bg-surface-2"
            >
              Team ↗
            </a>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted">
          {[
            company.tradeAccountNumber ? `Account ${company.tradeAccountNumber}` : null,
            company.owner ? `Managed by ${company.owner.name}` : "Nobody manages this account",
          ]
            .filter(Boolean)
            .join(" · ")}
          {company.externalUrl ? null : " · Not linked to Team"}
        </p>
      </header>

      <div className="flex-1 space-y-4 p-3">
        <KeyDetails company={company} />

        <details className="group rounded-lg border border-line bg-surface">
          <summary className={SUMMARY}>
            People
            <span className="font-normal text-faint">{company.contacts.length}</span>
            <Chevron />
          </summary>
          <div className="px-2 pb-2">
            <fieldset disabled={archived} className="m-0 border-0 p-0">
              <ContactsPanel
                companyId={company.id}
                companyName={company.name}
                contacts={company.contacts}
              />
            </fieldset>
          </div>
        </details>

        {/* Notes and history. No height of its own: the page scrolls, and the
            timeline is as long as it is. */}
        <section className="overflow-hidden rounded-lg border border-line bg-surface">
          <h2 className="border-b border-line px-4 py-2 text-xs font-semibold uppercase tracking-wide text-faint">
            Notes and history
          </h2>
          <Timeline
            companyId={company.id}
            companyName={company.name}
            contacts={company.contacts}
            taskBlockedReason={taskBlockedReason}
            branchCount={company.branchCount}
            composeMode={composeMode}
            onComposeMode={onComposeMode}
          />
        </section>

        <details className="group rounded-lg border border-line bg-surface">
          <summary className={SUMMARY}>
            More about this company
            <Chevron />
          </summary>
          <fieldset
            disabled={archived}
            className="m-0 flex min-w-0 flex-col gap-4 border-0 border-t border-line p-3 disabled:opacity-60"
          >
            {archived ? null : (
              <div className="grid grid-cols-2 gap-2">
                <PlainButton onClick={onEdit}>Edit details</PlainButton>
                <PlainButton onClick={onSalesCall}>Raise a sales call</PlainButton>
                {onArchive ? <PlainButton onClick={onArchive}>Archive</PlainButton> : null}
              </div>
            )}
            <GroupPanel company={company} />
            <AiSummaryPanel company={company} />
            <FollowPanel companyId={company.id} />
            <SalesCallsPanel companyId={company.id} onRaise={onSalesCall} />
            <AtRiskPanel company={company} />
            <FinancialsPanel company={company} />
            <CustomFieldsPanel company={company} />
            {user.role === "admin" ? <PerformancePanel companyId={company.id} /> : null}
            <CommercialPanels company={company} onCompose={onComposeMode} />
          </fieldset>
        </details>
      </div>

      {archived ? null : (
        <LogBar onCompose={onComposeMode} taskBlockedReason={taskBlockedReason} />
      )}
    </div>
  );
}

/**
 * The details that get somebody in the door, each one something to tap.
 *
 * A short list on purpose. Company number, VAT and who added the record are
 * desk questions and live under More -> Edit details; the phone shows what is
 * used from a car.
 */
function KeyDetails({ company }: { company: CompanyDetail }) {
  const filters = useQuery({
    queryKey: queryKeys.crmFilters,
    queryFn: crmApi.filters,
    staleTime: 5 * 60_000,
  });
  const picklists = filters.data?.picklists ?? {};

  const place = [company.city, company.region].filter(Boolean).join(", ");
  const tags = (
    [
      ["lifecycle_stage", company.lifecycleStage, ""],
      ["customer_type", company.customerType, ""],
      ["lead_priority", company.leadPriority, " priority"],
    ] as const
  )
    .filter(([, value]) => value)
    .map(([list, value, suffix]) => `${optionLabel(picklists[list] ?? [], value!)}${suffix}`);

  const rows: { label: string; value: string; href?: string }[] = [];
  if (company.phone) rows.push({ label: "Call", value: company.phone, href: `tel:${company.phone}` });
  if (company.email) rows.push({ label: "Email", value: company.email, href: `mailto:${company.email}` });
  if (place) {
    rows.push({
      label: "Where",
      value: place,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${company.name}, ${place}`)}`,
    });
  }
  if (company.domain) rows.push({ label: "Website", value: company.domain, href: `https://${company.domain}` });

  return (
    <section className="rounded-lg border border-line bg-surface">
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 border-b border-line px-4 py-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-muted-on-tint"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-faint">
          No number, email or address on file. Add them under More → Edit details.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((row) => (
            <li key={row.label}>
              <a
                href={row.href}
                target={row.href?.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="flex min-h-11 items-center gap-3 px-4 py-2 active:bg-surface-2"
              >
                <span className="w-16 shrink-0 text-xs text-faint">{row.label}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-brand-600 dark:text-brand-400">
                  {row.value}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Logging, along the bottom of the screen.
 *
 * Note, call and meeting each get a button because they are nearly everything
 * written from a phone; the rest -- WhatsApp, an email that missed the
 * helpdesk, a ticket handled elsewhere, a Lane task -- sit behind More. Each
 * one opens the timeline's own composer, which scrolls itself into view.
 */
function LogBar({
  onCompose,
  taskBlockedReason,
}: {
  onCompose: (mode: ComposerMode) => void;
  taskBlockedReason: string | null;
}) {
  const [more, setMore] = useState(false);

  const pick = (mode: ComposerMode): void => {
    setMore(false);
    onCompose(mode);
  };

  return (
    <div className="sticky bottom-0 z-20 border-t border-line bg-surface/95 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur">
      {more ? (
        <div className="mb-2 grid grid-cols-2 gap-2">
          <BarButton onClick={() => pick("whatsapp")}>WhatsApp</BarButton>
          <BarButton onClick={() => pick("email")}>Email</BarButton>
          <BarButton onClick={() => pick("external_ticket")}>Ticket</BarButton>
          <BarButton
            disabled={taskBlockedReason !== null}
            onClick={() => pick("task")}
          >
            Task
          </BarButton>
          {taskBlockedReason ? (
            <p className="col-span-2 text-[11px] text-faint">{taskBlockedReason}</p>
          ) : null}
        </div>
      ) : null}
      <div className="grid grid-cols-4 gap-2">
        <BarButton primary onClick={() => pick("note")}>Note</BarButton>
        <BarButton onClick={() => pick("call")}>Call</BarButton>
        <BarButton onClick={() => pick("meeting")}>Meeting</BarButton>
        <BarButton active={more} onClick={() => setMore((was) => !was)}>
          {more ? "Less" : "More"}
        </BarButton>
      </div>
    </div>
  );
}

function BarButton({
  onClick,
  primary,
  active,
  disabled,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        /* Forty pixels: a thumb's target without the bar becoming the
           heaviest thing on the screen, which at forty-four it was. */
        "h-10 rounded-lg text-sm font-medium disabled:opacity-40",
        primary
          ? "bg-brand-600 text-white active:bg-brand-700"
          : active
            ? "border border-brand-500 bg-surface text-brand-600 dark:text-brand-400"
            : "border border-line bg-surface text-content active:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

function PlainButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 rounded-lg border border-line bg-surface text-sm text-content active:bg-surface-2"
    >
      {children}
    </button>
  );
}

const SUMMARY =
  "flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium text-content [&::-webkit-details-marker]:hidden";

function Chevron() {
  return (
    <span aria-hidden className="ml-auto text-faint transition-transform group-open:rotate-90">
      ›
    </span>
  );
}
